/**
 * 公众号直连：com.wechatapp.php 回调与 componentapi 约定 action
 *
 * 需在 https://api.aicoiot.com/componentapi.php（或 VUE_APP_COMPONENT_API）实现：
 * - comwechat.get_config  GET：返回当前用户公众号配置（含 appid；敏感字段可用 has_* 标记，留空不返回明文）
 * - comwechat.save_config POST：appid, appsecret, encoding_aes_key, token（与公众平台服务器配置一致）
 * - comwechat.get_menu   POST：拉取微信侧自定义菜单（代理 menu/get 或 get_current_selfmenu_info）
 * - comwechat.set_menu   POST：body.menu 为 JSON 字符串，结构 { "button": [...] }（代理 menu/create）
 * 请求头与其它接口一致：Openid: 当前登录用户 openid
 *
 * 回调 URL：micoapp 站点 com.wechatapp.php?key=账户UID（UID 取用户 id / uid / openid，与 com.wechatapp.php 约定一致）
 */
export const WECHAT_APP_CALLBACK_BASE =
  process.env.VUE_APP_WECHAT_APP_CALLBACK_BASE ||
  "https://micoapp.aicoiot.com/app/com.wechatapp.php";

export const WECHAT_COMPONENT_API =
  process.env.VUE_APP_COMPONENT_API || "https://api.aicoiot.com/componentapi.php";

/** 与后端约定的 action（需在 componentapi.php 实现） */
export const WECHAT_MP_ACTION = {
  GET_CONFIG: "comwechat.get_config",
  SAVE_CONFIG: "comwechat.save_config",
  GET_MENU: "comwechat.get_menu",
  SET_MENU: "comwechat.set_menu",
  AUTH_URI: "authuri",
};

export function buildWechatAppCallbackUrl(accountUid) {
  const q = encodeURIComponent(String(accountUid || ""));
  return `${WECHAT_APP_CALLBACK_BASE}?key=${q}`;
}

export function resolveAccountUid(user) {
  if (!user || typeof user !== "object") return "";
  if (user.uid != null && String(user.uid) !== "") return String(user.uid);
  if (user.id != null && String(user.id) !== "") return String(user.id);
  if (user.openid) return String(user.openid);
  return "";
}

function normalizeMenuPayload(data) {
  if (!data) return { button: [] };
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (e) {
      return { button: [] };
    }
  }
  if (data.button) return data;
  if (data.menu && data.menu.button) return data.menu;
  if (data.selfmenu_info && data.selfmenu_info.button) {
    return { button: data.selfmenu_info.button };
  }
  return { button: [] };
}

export function parseLeafButton(b) {
  if (!b || typeof b !== "object") {
    return { type: "view", name: "", key: "", url: "", appid: "", pagepath: "" };
  }
  return {
    type: b.type || "view",
    name: b.name || "",
    key: b.key || "",
    url: b.url || "",
    appid: b.appid || "",
    pagepath: b.pagepath || "",
  };
}

/** 微信自定义菜单 → 界面行 */
export function menuFromWechat(menuData) {
  const { button } = normalizeMenuPayload(menuData);
  if (!Array.isArray(button)) return [];
  return button.map((b) => {
    if (b.sub_button && b.sub_button.length) {
      return {
        isSubMenu: true,
        name: b.name || "",
        children: b.sub_button.map(parseLeafButton),
      };
    }
    return {
      isSubMenu: false,
      ...parseLeafButton(b),
      children: [],
    };
  });
}

function leafToWechat(b) {
  const o = { name: (b.name || "").trim(), type: b.type || "view" };
  if (o.type === "click") {
    o.key = (b.key || "").trim();
  } else if (o.type === "view") {
    o.url = (b.url || "").trim();
  } else if (o.type === "miniprogram") {
    o.url = (b.url || "http://mp.weixin.qq.com").trim();
    o.appid = (b.appid || "").trim();
    o.pagepath = (b.pagepath || "").trim();
  } else {
    o.key = (b.key || "").trim();
  }
  return o;
}

/** 界面行 → 微信自定义菜单 */
export function menuToWechat(rows) {
  const button = (rows || []).map((row) => {
    if (row.isSubMenu) {
      return {
        name: (row.name || "").trim(),
        sub_button: (row.children || []).map(leafToWechat),
      };
    }
    return leafToWechat(row);
  });
  return { button };
}

export function validateWechatMenuRows(rows) {
  if (!rows || rows.length > 3) {
    return "一级菜单最多 3 个";
  }
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r.name || !String(r.name).trim()) {
      return `第 ${i + 1} 个一级菜单名称不能为空`;
    }
    if (r.isSubMenu) {
      const subs = r.children || [];
      if (subs.length < 1 || subs.length > 5) {
        return `「${r.name}」子菜单数量须为 1～5 个`;
      }
      for (let j = 0; j < subs.length; j++) {
        const s = subs[j];
        if (!s.name || !String(s.name).trim()) {
          return `「${r.name}」下第 ${j + 1} 个子菜单名称不能为空`;
        }
        const err = validateLeaf(s);
        if (err) return `「${r.name}」/「${s.name}」：${err}`;
      }
    } else {
      const err = validateLeaf(r);
      if (err) return `「${r.name}」：${err}`;
    }
  }
  return "";
}

function validateLeaf(b) {
  const t = b.type || "view";
  if (t === "click" && !(b.key && String(b.key).trim())) {
    return "click 类型需填写 key";
  }
  if (t === "view" && !(b.url && String(b.url).trim())) {
    return "view 类型需填写 url";
  }
  if (t === "miniprogram") {
    if (!(b.appid && String(b.appid).trim())) return "需填写小程序 appid";
    if (!(b.pagepath && String(b.pagepath).trim())) return "需填写小程序页面路径";
  }
  return "";
}
